// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { ApiService, BaseApiService } from '@fc-core/services/base-api.service';
import { AnyVehicle, MaintenanceNote } from '@fc-core/models';
import { expand, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { PhotoSource } from '@fc-vehicles/components/dialogs/vehicle-photo-upload-dialog/uploaded-list/photo.source';
import { AttachmentModel } from './attachment-model';

export interface NoteAttachment extends AttachmentModel {
  vehicle?: number;
}

export interface MaintenanceAttachment extends AttachmentModel {
  maintenanceAlert?: number;
}

@Injectable({ providedIn: 'root' })
@ApiService({})
export class MaintenanceNoteApiService extends BaseApiService {
  constructor(private http: HttpClient) {
    super();
  }

  static fromJSON(res: any): MaintenanceNote {
    if (res.created) {
      res.created = new Date(res.created);
    }
    if (res.modified) {
      res.modified = new Date(res.modified);
    }
    return res;
  }

  // TODO: replace hand-rolled pagination loop with fetchAllPages (@fc-shared/utils/request-utils/fetch-all-pages)
  loadAll(vehicle: AnyVehicle): Observable<MaintenanceNote[]> {
    return this.http.get(this.getVehicleUrl(vehicle.id)).pipe(
      expand((results: any) => {
        if (!!results['next']) {
          return this.http.get(results['next']);
        } else {
          return EMPTY;
        }
      }),
      map((res: any) => res['results']),
      map((res): MaintenanceNote[] => {
        return res.map((note: any) => MaintenanceNoteApiService.fromJSON(note));
      }),
    );
  }

  updateMaintenanceNoteAttachment(
    attachment: File,
    attachmentId: number,
    maintenanceNoteId: number,
    description?: string,
  ): Observable<NoteAttachment> {
    const formData = new FormData();
    if (attachment) formData.append('original', attachment);
    formData.append('description', description);
    return this.http.patch<NoteAttachment>(
      `${this.getBaseUrl()}/files/maintenance_notes/${maintenanceNoteId}/attachments/${attachmentId}/`,
      formData,
    );
  }
  updateMaintenanceNoteAttachments(
    files: PhotoSource[],
    maintenanceNoteId: number,
  ): Observable<NoteAttachment[]> {
    return forkJoin(
      files.map((file) => {
        if (file.id)
          return this.updateMaintenanceNoteAttachment(
            file.source,
            file.id,
            maintenanceNoteId,
            file.description,
          );
        return this.addMaintenanceNoteAttachment(
          file.source,
          maintenanceNoteId,
          file.description,
        );
      }),
    );
  }

  addMaintenanceNoteAttachment(
    attachment: File,
    maintenanceNoteId: number,
    description?: string,
  ): Observable<NoteAttachment> {
    const formData = new FormData();
    formData.append('original', attachment);
    formData.append('description', description);
    return this.http.post<NoteAttachment>(
      `${this.getBaseUrl()}/files/maintenance_notes/${maintenanceNoteId}/attachments/`,
      formData,
    );
  }

  addMaintenanceNoteAttachments(
    files: PhotoSource[],
    maintenanceNoteId: number,
  ): Observable<NoteAttachment[]> {
    return forkJoin(
      files.map((file) =>
        this.addMaintenanceNoteAttachment(
          file.source,
          maintenanceNoteId,
          file.description,
        ),
      ),
    );
  }

  create(note: MaintenanceNote): Observable<MaintenanceNote> {
    const notePayload = new FormData();
    Object.keys(note).forEach((key) => {
      if (note[key as keyof MaintenanceNote]) {
        notePayload.append(key, note[key as keyof MaintenanceNote] as any);
      }
    });
    return this.http
      .post(this.getVehicleUrl(note.vehicle), notePayload)
      .pipe(map(MaintenanceNoteApiService.fromJSON));
  }

  deleteAttachment(
    note: MaintenanceNote,
    attachment: NoteAttachment,
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.getBaseUrl()}/files/maintenance_notes/${note.id}/attachments/${
        attachment.id
      }/`,
    );
  }

  deleteManyAttachments(note: MaintenanceNote, attachments: NoteAttachment[]) {
    return forkJoin(
      attachments.map((attachment) => this.deleteAttachment(note, attachment)),
    );
  }

  delete(note: MaintenanceNote): Observable<MaintenanceNote> {
    return this.http
      .delete(this.getNoteUrl(note.vehicle, note))
      .pipe(map(() => note));
  }

  update(note: MaintenanceNote): Observable<MaintenanceNote> {
    const notePayload = new FormData();
    Object.keys(note).forEach((key) => {
      if (note[key as keyof MaintenanceNote]) {
        notePayload.append(key, note[key as keyof MaintenanceNote] as any);
      }
    });
    return this.http.put<MaintenanceNote>(
      this.getNoteUrl(note.vehicle, note),
      notePayload,
    );
  }

  protected getVehicleUrl(vehicleId: number): string {
    return `${this.getBaseUrl()}/maintenance/vehicle/${vehicleId}/note/`;
  }

  protected getNoteUrl(vehicleId: number, note: MaintenanceNote): string {
    return `${this.getBaseUrl()}/maintenance/vehicle/${vehicleId}/note/${
      note.id
    }/`;
  }
}
